import { Requirements } from "../../../../services/GroupGrowth/groupGrowth.types";
import { useState } from "react";
import { IconCarrot } from "../../../../common/icons/IconCarrot/IconCarrot";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import defaultContent from "../../../../content/ssiUplineReport";
import { useTranslation } from "react-i18next";
import DonutProgressCircle from "../../DonutProgressCard/DonutProgressCards";
import { useAppDispatch } from "../../../../store";
import { getLongPeriodFormat } from "../../../../common/util/period";
import { storedLocale } from "../../../Authentication/util";
import { Row } from "../InWindowWrapper/InWindowWrapper";
import { openProfileModal } from "../../../../reducers/ssiUplineReport";
import { track } from "../../../../common/util/tealium";
import TEALIUM from "../../../../common/enums/tealium-enums";
import "../../Shared/SsiUplineMobileCard.scss";

type Props = {
  row: Row;
  isLoading: boolean;
};

export const InWindowCard = ({ row, isLoading }: Props) => {
  const [isCollapsed, setIsCollasped] = useState(true);
  const { t } = useTranslation(["ssiUplineReport"]);
  const locale = storedLocale();
  const startDate = getLongPeriodFormat(row?.EligibilityWindow?.StartPeriod, locale);

  const dispatch = useAppDispatch();

  const renderMonthsQualified = () => {
    if (isLoading) {
      return <LeafRectangleLoader isLoading={true} width="8rem" height="1rem" margin="0" View={() => <></>} />;
    }
    if (row.EligibilityWindow.MarketHasGracePeriod) {
      return (
        <>
          <div className="SSIMobileCard__rowTitle">
            {t(
              `inSSIProgram.columns.monthsQualified`,
              defaultContent.inSSIProgram.columns["monthsQualified"] as string,
            )}
          </div>
          <div className={`SSIMobileCard__rowStat`}>
            <span className="SSIMobileCard__rowStat--highlight">{row.EligibilityWindow.AchievedMonths}</span>/
            {row.EligibilityWindow.AchievedMonthsWithGrace}
          </div>
        </>
      );
    } else {
      return (
        <>
          <div className="SSIMobileCard__rowTitle">
            {t(
              `inSSIProgram.columns.monthsQualified`,
              defaultContent.inSSIProgram.columns["monthsQualified"] as string,
            )}
          </div>
          <div className={`SSIMobileCard__rowStat`}>{row.EligibilityWindow.AchievedMonths}</div>
        </>
      );
    }
  };

  const renderAboNameNum = (aboName: string, aboNum: string) => {
    return (
      <div className="SSIMobileCard__column SSIMobileCard__aboNameNum">
        <span className="SSIMobileCard__column--title">
          <LeafRectangleLoader
            isLoading={isLoading}
            width="8rem"
            height="1rem"
            margin="0"
            View={() => <>{aboName}</>}
          />
        </span>
        <span className="SSIMobileCard__column--subtext">
          <LeafRectangleLoader
            isLoading={isLoading}
            width="4rem"
            height="1rem"
            margin=".5rem 0 0 0"
            View={() => <>#{aboNum}</>}
          />
        </span>
      </div>
    );
  };

  const renderProfile = () => {
    return (
      <LeafRectangleLoader
        isLoading={isLoading}
        width="5rem"
        height="1.6rem"
        margin="0"
        View={() => (
          <button
            id={`InWindowCardProfileButton${row.AboNum}`}
            onClick={() => {
              dispatch(
                openProfileModal({
                  aboNo: row.AboNum,
                  affNo: row.Aff,
                  sponsorNo: row.SponsorNum,
                  sponsorAff: row.Aff,
                  eligibilityWindow: row.EligibilityWindow,
                  showEligibilityWindow: true,
                  show: true,
                  previousElementID: `InWindowCardProfileButton${row.AboNum}`,
                }),
              );
              track(TEALIUM.U_TAG_METHOD.LINK, "", "in-window", "SSI_Upline_Report", "", "ABO_profile");
            }}
          >
            <div className="SSIMobileCard__profileButtonContent">
              <div>
                <span>{t("profile", defaultContent.profile as string)}</span>
                <div className="SSIMobileCard__profileButtonContent--underline"></div>
              </div>
              <IconCarrot orientation="right" color="black" />
            </div>
          </button>
        )}
      />
    );
  };

  const renderRequirements = (requirements: Requirements, abo: string) => {
    return isLoading ? (
      <DonutProgressCircle label="personal" currentAmount={0} targetAmount={0} ariaTitle="" isLoading={true} />
    ) : (
      <>
        {requirements.VolumeRequirements.map((req) => {
          return (
            <DonutProgressCircle
              met={req.Met}
              label={req.Name}
              currentAmount={req.Volume.Current.Actual}
              targetAmount={req.Volume.Target.Actual}
              ariaTitle=""
              key={`DonutProgressCircle-mobile-${abo}-${req.Name}`}
            />
          );
        })}
        {requirements.PercentRequirements ? (
          requirements.PercentRequirements.map((req) => {
            return (
              <DonutProgressCircle
                met={req.Met}
                label={req.Name}
                currentAmount={req.Percent.Current.Actual}
                targetAmount={req.Percent.Target.Actual}
                ariaTitle=""
                key={`DonutProgressCircle-vcs-mobile-${abo}`}
              />
            );
          })
        ) : (
          <></>
        )}
      </>
    );
  };

  const renderStats = (
    title: string,
    isLoading: boolean = false,
    text?: string,
    stat?: number,
    highlight?: boolean,
    abo?: string,
  ) => {
    if (isLoading) {
      return <LeafRectangleLoader isLoading={true} width="8rem" height="1rem" margin="0" View={() => <></>} />;
    }

    return (
      <>
        <div className="SSIMobileCard__rowTitle">
          {t(`inSSIProgram.columns.${title}`, defaultContent.inSSIProgram.columns[title] as string)}
        </div>
        {abo ? (
          <div className="SSIMobileCard__rowStat SSIMobileCard__rowStat--abo">
            <span>{text}</span>
          </div>
        ) : (
          <div className={`SSIMobileCard__rowStat ${highlight ? "SSIMobileCard__rowStat--highlight" : ""}`}>
            {text ? <>{text}</> : <>{stat}</>}
          </div>
        )}
      </>
    );
  };

  return (
    <section className={`SSIMobileCard SSIMobileCard--mobile ${row.IsHighlighted ? "SSIMobileCard__highlighted" : ""}`}>
      <div className={`SSIMobileCard__MobileRow SSIMobileCard__MobileRow--noBorder`}>
        {renderAboNameNum(row.AboName, row.AboNum)}
        {renderProfile()}
      </div>
      <div className={`SSIMobileCard__MobileRow--center`}>{renderRequirements(row.Requirements, row.AboNum)}</div>
      <div className={`SSIMobileCard__MobileRow ${isCollapsed ? "SSIMobileCard__MobileRow--noBorder" : ""}`}>
        {/* {renderStats("monthsQualified", isLoading, undefined, monthsQualified, isPaymentMonth)} */}
        {renderMonthsQualified()}
      </div>

      <div
        className={`SSIMobileCard__expandableContent ${
          isCollapsed ? "SSIMobileCard__expandableContent--hidden" : "SSIMobileCard__expandableContent--visible"
        } `}
      >
        <div className="SSIMobileCard__MobileRow">{renderStats("programStartDate", isLoading, startDate)}</div>
        <div className="SSIMobileCard__MobileRow">
          {renderStats("paymentsReceived", isLoading, undefined, row.EligibilityWindow.PaymentsEarned)}
        </div>
        <div className="SSIMobileCard__MobileRow SSIMobileCard__MobileRow--noBorder">
          {renderStats("sponsor", isLoading, row.SponsorName, undefined, undefined, "sponsor")}
        </div>
      </div>

      <button className="SSIMobileCard__expandButton" onClick={() => setIsCollasped(!isCollapsed)}>
        <IconCarrot orientation={isCollapsed ? "down" : "up"} color={"black"} />
      </button>
    </section>
  );
};
